/**
 * The role picker of a business system (5 Oct, product owner's first test).
 *
 * People asking for 365FO, Bluestar or Power BI rarely know the role names,
 * and the form only has a free-text "Roles needed". So they pick from the
 * lists the approval matrix defines — 365FO by functional area (example roles
 * as the hint), Bluestar by area with View or Design, Power BI by data area —
 * or type it under "Other". The server writes the choices into "Roles needed"
 * as readable lines; for a new hire the step's owner reads them.
 *
 * Bluestar's global roles / add-ons (7 Oct demo, Helle: "release, OpenID…,
 * on top of the workspaces") follow the areas in their own group: plain tick
 * boxes, no View or Design.
 *
 * `areasOnly` (7 Oct, UAT walkthrough, the New hire checklist): the areas as
 * plain tick boxes and "Other", nothing else — no title or hint on screen,
 * no search, no example roles. HR picks the area; the step's owner picks the
 * roles at "Open and complete", where the full picker stays.
 *
 * 365FO sub-roles (2 Nov, Helle): a ticked area opens its real roles as tick
 * boxes. The approver approves the area; the sub-roles say what is given.
 * None ticked: the area as a whole, as before.
 *
 * `title` / `required` / `error` (8 Oct): the same picker standing in for a
 * form's "Roles or access needed" when the system is Bluestar
 * (`BluestarAreasField`), titled as the form's question.
 */

import { Checkbox, Field, Input, Radio, RadioGroup, Typography } from "@norican/react";
import { useState } from "react";

import { useT } from "@/i18n";
import type { AccessRoleList, AccessRoleOption } from "@/lib/api";

import {
  filterRoleOptions,
  type RoleSelection,
  setPreApproved,
  setRoleLevel,
  toggleRole,
  toggleSubrole,
} from "../lib/access";

export interface RolePickerProps {
  name: string;
  list: AccessRoleList;
  value: RoleSelection;
  onChange: (value: RoleSelection) => void;
  /** New: the picker is a help, not a question ("if you know"). */
  optional?: boolean;
  /** "Other (type it)"; off at "Open and complete", where the form's own field takes it. */
  showOther?: boolean;
  /**
   * "Open and complete" (5 Oct): ask, on a Power BI model a held role
   * pre-approves, whether the person already holds it.
   */
  askPreApproval?: boolean;
  /** A line under the title (the default says the picker is optional). */
  hint?: string;
  /** Areas and "Other" only: no visible title or hint, no search, no example roles. */
  areasOnly?: boolean;
  /** The title instead of "Roles in <system>" (a form's own question). */
  title?: string;
  /** Marked required, as a form field is. */
  required?: boolean;
  /** A validation message under the picker, already translated. */
  error?: string;
}

export function RolePicker({
  name,
  list,
  value,
  onChange,
  optional = false,
  showOther = true,
  askPreApproval = false,
  hint,
  areasOnly = false,
  title,
  required = false,
  error,
}: RolePickerProps) {
  const { t } = useT("tickets");
  const [query, setQuery] = useState("");
  const levels = list.levels ?? [];
  const options = filterRoleOptions(list.options ?? [], query);
  const areas = options.filter((option) => option.addon !== true);
  const addons = options.filter((option) => option.addon === true);
  const chosen = new Map(value.choices.map((choice) => [choice.id, choice]));
  const row = (option: AccessRoleOption) => {
    const picked = chosen.get(option.id);
    const optionLevels = option.addon === true ? [] : levels;
    const subroles = areasOnly ? [] : (option.roles ?? []);
    return (
      <li key={option.id} className="flex flex-col gap-1">
        <Checkbox.Root
          checked={picked !== undefined}
          onCheckedChange={() => onChange(toggleRole(value, option.id, optionLevels))}
          label={
            option.group && option.addon !== true
              ? `${option.label} (${option.group})`
              : option.label
          }
        />
        {option.hint && !areasOnly && !(picked !== undefined && subroles.length > 0) ? (
          <Typography as="span" variant="caption" tone="muted" className="ps-7">
            {option.hint}
          </Typography>
        ) : null}
        {picked !== undefined && subroles.length > 0 ? (
          <fieldset className="m-0 flex flex-col gap-1 border-0 p-0 ps-7">
            <legend className="p-0">
              <Typography as="span" variant="caption" tone="muted">
                {t("access.wizard.roles.subroles", { area: option.label })}
              </Typography>
            </legend>
            <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
              {subroles.map((subrole) => (
                <Checkbox.Root
                  key={subrole}
                  checked={(picked.subroles ?? []).includes(subrole)}
                  onCheckedChange={() =>
                    onChange(toggleSubrole(value, option.id, subrole, subroles))
                  }
                  label={subrole}
                />
              ))}
            </div>
          </fieldset>
        ) : null}
        {picked !== undefined && askPreApproval && option.pre_approval ? (
          <div className="ps-7">
            <Checkbox.Root
              checked={picked.pre_approved === true}
              onCheckedChange={(checked) =>
                onChange(setPreApproved(value, option.id, checked === true))
              }
              label={t("access.approvals.preApprovedTick", {
                condition: option.pre_approval,
              })}
            />
          </div>
        ) : null}
        {picked !== undefined && optionLevels.length > 0 ? (
          <RadioGroup
            value={picked.level ?? optionLevels[0] ?? ""}
            onValueChange={(level) => onChange(setRoleLevel(value, option.id, String(level)))}
            className="flex flex-wrap gap-4 ps-7"
            aria-label={t("access.wizard.roles.level", { area: option.label })}
          >
            {optionLevels.map((level) => (
              <Radio.Root key={level} value={level} label={level} />
            ))}
          </RadioGroup>
        ) : null}
      </li>
    );
  };
  return (
    <fieldset className="border-border-subtle m-0 flex min-w-0 flex-col gap-3 rounded-md border p-3">
      <legend className={areasOnly ? "sr-only" : "px-1"}>
        <Typography as="span" variant="body-sm" className="font-medium">
          {title ??
            (optional
              ? t("access.wizard.roles.titleOptional", { system: list.label })
              : t("access.wizard.roles.title", { system: list.label }))}
          {required ? <span aria-hidden="true"> *</span> : null}
        </Typography>
      </legend>
      {areasOnly ? null : (
        <Typography as="p" variant="caption" tone="muted">
          {hint ?? t("access.wizard.roles.hint")}
        </Typography>
      )}
      {!areasOnly && (list.options ?? []).length > 8 ? (
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("access.wizard.roles.search")}
          aria-label={t("access.wizard.roles.search")}
        />
      ) : null}
      <ul className="m-0 flex max-h-72 list-none flex-col gap-2 overflow-y-auto p-0">
        {areas.map(row)}
      </ul>
      {addons.length > 0 ? (
        <div className="flex flex-col gap-2">
          <Typography as="h4" variant="body-sm" className="font-medium">
            {t("access.wizard.roles.addons")}
          </Typography>
          <Typography as="p" variant="caption" tone="muted">
            {t("access.wizard.roles.addonsHint")}
          </Typography>
          <ul className="m-0 flex max-h-72 list-none flex-col gap-2 overflow-y-auto p-0">
            {addons.map(row)}
          </ul>
        </div>
      ) : null}
      {showOther ? (
        <Field name={`${name}-other`} label={t("access.wizard.roles.other")}>
          <Input
            value={value.other}
            maxLength={2000}
            onChange={(event) => onChange({ ...value, other: event.target.value })}
          />
        </Field>
      ) : null}
      {error ? (
        <Typography as="p" variant="caption" tone="danger">
          {error}
        </Typography>
      ) : null}
    </fieldset>
  );
}
